import { useLayoutEffect, useRef, useState, useEffect } from "react";
import "./A4Preview.css";
import { SENDER } from "../data/catalog";
import { eur, formatDateIT } from "../lib/format";
import { rowTotal, monthlyRowTotal, modeSuffix, computeTotals } from "../lib/pricing";
import { applyRichTextToElement, richRunsForSlice, hasRichStyles } from "../lib/richText";

const PXMM = 96 / 25.4;
const PAGE_H = 297 * PXMM;
const COL_PAD_TOP = 11 * PXMM;
const COL_PAD_BOTTOM = 6 * PXMM;
const FOOTER_RESERVE = 15 * PXMM;
const GAP = 5 * PXMM;

const LogoMark = ({ logo }) =>
  logo ? (
    <img src={logo} alt="We Are Albero" className="a4-logo-img" />
  ) : (
    <span className="a4-logo-word">
      <em>we are</em>/Albero
    </span>
  );

const Band = ({ logo, quote }) => (
  <div className="a4-band">
    <div className="a4-band-top">
      <LogoMark logo={logo} />
      <div className="a4-band-meta">
        <span className="a4-meta-k">Data</span>
        <span className="a4-meta-v">{formatDateIT(quote.date)}</span>
      </div>
    </div>
    <div className="a4-band-info">
      <div className="a4-band-col">
        <div className="a4-info-label">We Are Albero</div>
        <div className="a4-info-body">
          {SENDER.operativa}
          <br />
          {SENDER.email} · {SENDER.phone}
          <br />
          {SENDER.vat}
        </div>
      </div>
      <div className="a4-band-col a4-band-col-right">
        <div className="a4-info-label">Spett.le</div>
        <div className="a4-info-body">
          <span className="a4-client-name">
            {quote.client?.name || "Nome cliente"}
          </span>
          {quote.client?.vat && (
            <>
              <br />
              P.IVA {quote.client.vat}
            </>
          )}
          {quote.client?.taxCode && (
            <>
              <br />
              C.F. {quote.client.taxCode}
            </>
          )}
          {quote.client?.address && (
            <>
              <br />
              {quote.client.address}
            </>
          )}
          {(quote.client?.cap ||
            quote.client?.city ||
            quote.client?.province) && (
            <>
              <br />
              {[quote.client?.cap, quote.client?.city, quote.client?.province]
                .filter(Boolean)
                .join(" ")}
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);

const RunHeader = ({ logo, quote }) => (
  <div className="a4-run">
    <LogoMark logo={logo} />
    <span className="a4-run-meta">
      {quote.projectTitle || "Proposta economica"}
    </span>
  </div>
);

const Footer = ({ page, total }) => (
  <div className="a4-footer">
    <span>
      We Are Albero · Via Calpurnio Fiamma, 33 Roma (RM) · {SENDER.email}
    </span>
    <span className="a4-pageno">
      {String(page).padStart(2, "0")} / {String(total).padStart(2, "0")}
    </span>
  </div>
);

const RichDescription = ({ fullText, text, runs, offset = 0 }) => {
  const source = String(fullText || "").replace(/\r\n?/g, "\n");
  const shown = String(text || "").replace(/\r\n?/g, "\n");
  const start = Math.max(0, Number(offset) || 0);
  const end = Math.min(source.length, start + shown.length);
  const sliced = richRunsForSlice(source, runs, start, end);

  if (!hasRichStyles(sliced)) return shown;

  const parts = [];
  let cursor = start;
  sliced.forEach((run, idx) => {
    if (run.start > cursor) {
      parts.push(source.slice(cursor, run.start));
    }

    const style = {};
    if (run.bold) style.fontWeight = 700;
    if (run.italic) style.fontStyle = "italic";
    const decorations = [];
    if (run.underline) decorations.push("underline");
    if (run.strikethrough) decorations.push("line-through");
    if (decorations.length) style.textDecoration = decorations.join(" ");

    parts.push(
      <span key={`rt-${idx}-${run.start}`} style={style}>
        {source.slice(Math.max(cursor, run.start), run.end)}
      </span>,
    );
    cursor = Math.max(cursor, run.end);
  });

  if (cursor < end) parts.push(source.slice(cursor, end));
  return parts;
};

const QuoteItem = ({
  it,
  alt,
  description,
  descriptionOffset = 0,
  continuation = false,
  suffix = "",
}) => (
  <div
    className={`a4-item ${alt ? "a4-item-alt" : ""} ${
      continuation ? "a4-item-continuation" : ""
    }`}
  >
    <div className="a4-item-main">
      <div className="a4-item-name">
        {it.name}
        {continuation ? (
          <span className="a4-item-continued"> — continua</span>
        ) : (
          (Number(it.qty) || 1) > 1 && (
            <span className="a4-item-qty"> × {Number(it.qty)}</span>
          )
        )}
      </div>
      {description && (
        <div className="a4-item-desc">
          <RichDescription
            fullText={it.description || ""}
            text={description}
            runs={it.descriptionRich || []}
            offset={descriptionOffset}
          />
        </div>
      )}
    </div>
    {it.showPrice && !continuation && (
      <div className="a4-item-price">
        <span className="a4-item-amount">{eur(rowTotal(it))}</span>
        {it.recurring ? (
          <span className="a4-item-suffix">attivazione una tantum</span>
        ) : (
          suffix && <span className="a4-item-suffix">{suffix.trim()}</span>
        )}
      </div>
    )}
  </div>
);

export const A4Preview = ({ quote, logo }) => {
  const totals = computeTotals(quote);
  const suffix = modeSuffix(quote.priceMode);
  const reserved = quote.reservedPrice && totals.hasDiscount;

  const namedItems = (quote.items || []).filter((it) => it.name);
  const hasItems = namedItems.length > 0;

  /* ---- build content blocks (follow editor order) ---- */
  const blocks = [];

  blocks.push({
    key: "intro",
    keepWithNext: true,
    node: (
      <div className="a4-intro">
        <div className="a4-eyebrow">Proposta economica</div>
        <h1 className="a4-project">{quote.projectTitle || "Nome progetto"}</h1>
      </div>
    ),
  });

  if (quote.tagline)
    blocks.push({
      key: "tagline",
      keepWithNext: false,
      node: <p className="a4-tagline">{quote.tagline}</p>,
    });

  if (!hasItems)
    blocks.push({
      key: "empty",
      keepWithNext: false,
      node: (
        <div className="a4-empty">
          Aggiungi voci dal catalogo per popolare il preventivo.
        </div>
      ),
    });

  let prevCat = null;
  let secIdx = 0;
  let itemIdx = 0;
  namedItems.forEach((it) => {
    const cat = it.category || "Personalizzata";
    if (cat !== prevCat) {
      secIdx += 1;
      prevCat = cat;
      blocks.push({
        key: `sec-${secIdx}-${cat}`,
        keepWithNext: true,
        node: (
          <div className="a4-section-head">
            <span className="a4-section-index">
              {String(secIdx).padStart(2, "0")}
            </span>
            <span className="a4-section-title">{cat}</span>
            <span className="a4-section-line" />
          </div>
        ),
      });
    }
    const alt = itemIdx % 2 === 1;
    itemIdx += 1;
    blocks.push({
      key: `it-${it.id}`,
      kind: "item",
      item: it,
      alt,
      keepWithNext: false,
      node: (
        <QuoteItem
          it={it}
          alt={alt}
          description={it.description || ""}
          suffix={suffix}
        />
      ),
    });
  });

  const catKeys = Object.keys(totals.byCategory).filter(
    (c) => totals.byCategory[c].names.length,
  );

  // Riepilogo delle lavorazioni: resta prima di condizioni e note.
  // Il totale viene invece spostato nel blocco finale insieme a firma e abbonamenti.
  if (hasItems) {
    blocks.push({
      key: "contract-summary",
      keepWithNext: false,
      node: (
        <div className="a4-contract a4-contract-summary">
          <div className="a4-contract-title">Riepilogo delle lavorazioni</div>
          <div className="a4-contract-rows">
            {catKeys.map((cat) => {
              const info = totals.byCategory[cat];
              return (
                <div className="a4-contract-row" key={cat}>
                  <div className="a4-contract-cat">
                    <b>{cat}</b>
                    <span>{info.names.join(", ")}</span>
                  </div>
                  {quote.showItemPrices && (
                    <div className="a4-contract-catprice">
                      {eur(info.total)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ),
    });
  }

  blocks.push({
    key: "conditions",
    keepWithNext: false,
    node: (
      <div>
        <div className="a4-conditions">
          {quote.validUntil && (
            <div className="a4-cond">
              <span className="a4-cond-label">Offerta valida fino al</span>
              <span className="a4-cond-value">
                {formatDateIT(quote.validUntil)}
              </span>
            </div>
          )}
          {quote.processingTime && (
            <div className="a4-cond">
              <span className="a4-cond-label">Tempi di elaborazione</span>
              <span className="a4-cond-value">{quote.processingTime}</span>
            </div>
          )}
          {quote.paymentTerms && (
            <div className="a4-cond">
              <span className="a4-cond-label">Termini di pagamento</span>
              <span className="a4-cond-value">{quote.paymentTerms}</span>
            </div>
          )}
          <div className="a4-cond">
            <span className="a4-cond-label">Bonifico IBAN</span>
            <span className="a4-cond-value">{SENDER.iban}</span>
          </div>
        </div>
        {totals.hasRecurring && (
          <div className="a4-payment-note">
            <span className="a4-cond-label">
              Modalità di pagamento — servizi in abbonamento
            </span>
            <p>
              I prodotti a canone mensile (Lynfa, Hardware e campagne
              marketing) verranno addebitati al cliente all'inizio di ogni mese
              tramite sistema SDD (addebito diretto SEPA). Per la disdetta del
              servizio è richiesto un preavviso di 30 giorni tramite email.
            </p>
          </div>
        )}
        {quote.notes && <div className="a4-notes">{quote.notes}</div>}
      </div>
    ),
  });

  // Questo blocco deve restare intero: totale, eventuali abbonamenti e firma
  // vengono sempre impaginati sulla stessa pagina.
  if (hasItems) {
    blocks.push({
      key: "acceptance",
      keepWithNext: false,
      node: (
        <div className="a4-acceptance">
          <div className="a4-acceptance-title">Riepilogo economico e accettazione</div>

          <div className="a4-final-total">
            <div className="a4-final-total-label">Totale</div>
            <div className="a4-final-total-value">
              {reserved && (
                <span className="a4-final-strike">
                  {eur(totals.subtotal)}
                  {suffix}
                </span>
              )}
              {reserved && (
                <span className="a4-reserved-tag">Prezzo riservato</span>
              )}
              <span className="a4-final-total-amount">
                {eur(totals.final)}
                {suffix}
              </span>
            </div>
          </div>

          {totals.recurringItems.length > 0 && (
            <div className="a4-subscriptions a4-subscriptions-final">
              <div className="a4-sub-title">Servizi in abbonamento</div>
              <div className="a4-sub-note">
                Importi mensili ricorrenti, non inclusi nel totale una tantum.
              </div>
              <div className="a4-sub-rows">
                {totals.recurringItems.map((it) => (
                  <div className="a4-sub-row" key={it.id}>
                    <span className="a4-sub-name">
                      {it.name}
                      {(Number(it.monthlyQty) || 1) > 1 && (
                        <span className="a4-sub-qty">
                          {" "}
                          × {Number(it.monthlyQty)}
                        </span>
                      )}
                    </span>
                    <span className="a4-sub-price">
                      {eur(monthlyRowTotal(it))} /mese
                    </span>
                  </div>
                ))}
              </div>
              <div className="a4-sub-total">
                <span>Totale mensile</span>
                <span className="a4-sub-total-amount">
                  {eur(totals.monthlyTotal)} /mese
                </span>
              </div>
            </div>
          )}

          <div className="a4-sign-wrap a4-sign-wrap-final">
            <div className="a4-thanks">
              Nel ringraziarvi, vi porgiamo cordiali saluti.
            </div>
            <div className="a4-sign-line">Firma di accettazione</div>
            <p className="a4-signature-quote">
              “We are Architects. 3D Artists, creative specialists. We strongly
              believe in the power of visual representations as a tool to give life
              to professional and companies projects”
            </p>
          </div>
        </div>
      ),
    });
  } else {
    blocks.push({
      key: "signature",
      keepWithNext: false,
      node: (
        <div className="a4-sign-wrap">
          <div className="a4-thanks">
            Nel ringraziarvi, vi porgiamo cordiali saluti.
          </div>
          <div className="a4-sign-line">Firma di accettazione</div>
          <p className="a4-signature-quote">
            “We are Architects. 3D Artists, creative specialists. We strongly
            believe in the power of visual representations as a tool to give life
            to professional and companies projects”
          </p>
        </div>
      ),
    });
  }

  /* ---- pagination (page-aware; long descriptions may continue) ---- */
  const blockMap = {};
  blocks.forEach((b) => {
    blockMap[b.key] = b;
  });

  const toPageEntry = (b) =>
    b.kind === "item"
      ? {
          key: b.key,
          kind: "item",
          description: b.item.description || "",
          descriptionOffset: 0,
          continuation: false,
        }
      : { key: b.key };

  const measureRef = useRef(null);
  const [pages, setPages] = useState(() => [blocks.map(toPageEntry)]);
  const [fontTick, setFontTick] = useState(0);

  useEffect(() => {
    if (document.fonts && document.fonts.ready)
      document.fonts.ready.then(() => setFontTick((t) => t + 1));
  }, []);

  const sig = JSON.stringify(quote) + "|" + suffix + "|" + fontTick;

  useLayoutEffect(() => {
    const root = measureRef.current;
    if (!root) return;
    const bandH = root.querySelector('[data-m="band"]')?.offsetHeight || 0;
    const runH = root.querySelector('[data-m="run"]')?.offsetHeight || 0;
    const heights = blocks.map(
      (_, i) =>
        root.querySelector(`[data-mi="${i}"]`)?.getBoundingClientRect()
          .height || 0,
    );

    const usableFirst =
      PAGE_H - bandH - COL_PAD_TOP - COL_PAD_BOTTOM - FOOTER_RESERVE;
    const usableRest =
      PAGE_H - runH - COL_PAD_TOP - COL_PAD_BOTTOM - FOOTER_RESERVE;

    const measureItem = (index, text, continuation, descriptionOffset = 0) => {
      const selector = continuation ? `[data-mic="${index}"]` : `[data-mi="${index}"]`;
      const source = root.querySelector(selector);
      if (!source) return heights[index] || 0;
      const clone = source.cloneNode(true);
      clone.removeAttribute(continuation ? "data-mic" : "data-mi");
      const desc = clone.querySelector(".a4-item-desc");
      if (desc) {
        if (text) {
          const fullText = String(blocks[index]?.item?.description || "").replace(/\r\n?/g, "\n");
          applyRichTextToElement(
            desc,
            fullText,
            blocks[index]?.item?.descriptionRich || [],
            descriptionOffset,
            descriptionOffset + String(text).replace(/\r\n?/g, "\n").length,
          );
        } else {
          desc.remove();
        }
      }
      root.appendChild(clone);
      const value = clone.getBoundingClientRect().height || 0;
      clone.remove();
      return value;
    };

    const splitToFit = (index, text, continuation, maxHeight, descriptionOffset = 0) => {
      const sourceText = String(text || "").replace(/\r\n?/g, "\n");

      // Usiamo i confini delle parole ma tagliamo il testo originale, senza
      // ricostruirlo con join(): così restano intatti a-capo, righe vuote,
      // punti elenco, trattini e spaziatura inseriti nella descrizione.
      const boundaries = [];
      const tokenRe = /\S+\s*/g;
      let match;
      while ((match = tokenRe.exec(sourceText))) {
        boundaries.push(match.index + match[0].length);
      }
      if (!boundaries.length) return { head: "", tail: "", count: 0 };

      let lo = 1;
      let hi = boundaries.length;
      let best = 0;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const candidate = sourceText.slice(0, boundaries[mid - 1]);
        if (measureItem(index, candidate, continuation, descriptionOffset) <= maxHeight) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      if (!best) return { head: "", tail: sourceText, count: 0 };

      let cutAt = boundaries[best - 1];

      // Se il taglio cadrebbe appena dopo l'inizio di una nuova riga (es.
      // "• voce", "- voce", "1. voce"), preferiamo mandare tutta quella
      // riga alla pagina successiva. Evita punti elenco/orfani a fondo pagina.
      const provisionalHead = sourceText.slice(0, cutAt);
      const lastNewline = provisionalHead.lastIndexOf("\n");
      if (lastNewline >= 0 && cutAt < sourceText.length) {
        const trailingLine = provisionalHead.slice(lastNewline + 1).trim();
        const trailingWords = trailingLine.match(/\S+/g) || [];
        if (trailingWords.length > 0 && trailingWords.length <= 5) {
          const lineCut = lastNewline + 1;
          if (lineCut > 0) cutAt = lineCut;
        }
      }

      const head = sourceText.slice(0, cutAt);
      return {
        head,
        tail: sourceText.slice(cutAt),
        count: (head.match(/\S+/g) || []).length,
      };
    };

    const result = [];
    let cur = [];
    let h = 0;
    let cap = usableFirst;

    const flush = () => {
      if (cur.length) result.push(cur);
      cur = [];
      h = 0;
      cap = usableRest;
    };

    const add = (entry, height) => {
      h += (cur.length ? GAP : 0) + height;
      cur.push(entry);
    };

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];

      if (block.kind !== "item") {
        const hi = heights[i];
        let extra = 0;
        if (block.keepWithNext && blocks[i + 1]) {
          const next = blocks[i + 1];
          if (next.kind === "item") {
            const previewSource = String(next.item.description || "").replace(/\r\n?/g, "\n");
            const tokenRe = /\S+\s*/g;
            let token;
            let count = 0;
            let cutAt = 0;
            while (count < 20 && (token = tokenRe.exec(previewSource))) {
              cutAt = token.index + token[0].length;
              count += 1;
            }
            const previewWords = previewSource.slice(0, cutAt);
            extra = GAP + measureItem(i + 1, previewWords, false, 0);
          } else {
            extra = GAP + (heights[i + 1] || 0);
          }
        }
        const need = (cur.length ? GAP : 0) + hi + extra;
        if (cur.length && h + need > cap) flush();
        add({ key: block.key }, hi);
        continue;
      }

      let remaining = String(block.item.description || "").replace(/\r\n?/g, "\n");
      let remainingOffset = 0;
      let continuation = false;

      // Items without a description are ordinary indivisible rows.
      if (!remaining.trim()) {
        const itemH = measureItem(i, "", false);
        if (cur.length && h + (cur.length ? GAP : 0) + itemH > cap) flush();
        add(
          { key: block.key, kind: "item", description: "", descriptionOffset: 0, continuation: false },
          itemH,
        );
        continue;
      }

      while (remaining) {
        const gap = cur.length ? GAP : 0;
        const available = cap - h - gap;
        const wholeH = measureItem(i, remaining, continuation, remainingOffset);

        if (wholeH <= available) {
          add(
            {
              key: block.key,
              kind: "item",
              description: remaining,
              descriptionOffset: remainingOffset,
              continuation,
            },
            wholeH,
          );
          remaining = "";
          break;
        }

        // Comportamento v4: se nella pagina corrente c'è spazio utile,
        // iniziamo subito la voce e spezziamo la descrizione tra le pagine.
        // Il taglio avviene sempre sui confini delle parole e preserva gli
        // a-capo realmente presenti nel testo originale.
        let part = splitToFit(
          i,
          remaining,
          continuation,
          available,
          remainingOffset,
        );

        // Evita soltanto frammenti troppo piccoli a fondo pagina. È la stessa
        // protezione della v4: se entrano meno di 10 parole, la voce parte dalla
        // pagina successiva; altrimenti utilizza lo spazio residuo disponibile.
        if (cur.length && part.count > 0 && part.count < 10) {
          flush();
          continue;
        }

        if (!part.head) {
          if (cur.length) {
            flush();
            continue;
          }

          // Fallback estremo: forza l'avanzamento di almeno una parola senza
          // modificare spazi, a-capo o indici del rich text.
          const firstToken = /\S+\s*/.exec(remaining);
          const cutAt = firstToken
            ? firstToken.index + firstToken[0].length
            : remaining.length;
          part = {
            head: remaining.slice(0, cutAt),
            tail: remaining.slice(cutAt),
            count: 1,
          };
        }

        const partH = measureItem(i, part.head, continuation, remainingOffset);
        add(
          {
            key: block.key,
            kind: "item",
            description: part.head,
            descriptionOffset: remainingOffset,
            continuation,
          },
          partH,
        );
        remainingOffset += part.head.length;
        remaining = part.tail;
        continuation = true;
        if (!remaining.trim()) remaining = "";
        if (remaining) flush();
      }
    }

    if (cur.length) result.push(cur);
    if (!result.length) result.push([]);

    setPages((prev) =>
      JSON.stringify(prev) === JSON.stringify(result) ? prev : result,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const total = pages.length;
  const clientName = quote.client?.name || "Nome cliente";

  return (
    <div className="a4-doc" data-testid="a4-doc">
      {/* hidden measuring layer */}
      <div className="a4-measure" ref={measureRef} aria-hidden="true">
        <div data-m="band">
          <Band logo={logo} quote={quote} />
        </div>
        <div data-m="run">
          <RunHeader logo={logo} quote={quote} />
        </div>
        {blocks.map((b, i) => (
          <div key={b.key}>
            <div className="a4-measure-col" data-mi={i}>
              {b.node}
            </div>
            {b.kind === "item" && (
              <div className="a4-measure-col" data-mic={i}>
                <QuoteItem
                  it={b.item}
                  alt={b.alt}
                  description={b.item.description || ""}
                  descriptionOffset={0}
                  continuation
                  suffix={suffix}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* cover page (not numbered) */}
      <div className="a4-page a4-cover" data-testid="a4-cover">
        <div className="a4-cover-inner">
          <div className="a4-cover-logo">
            <LogoMark logo={logo} />
          </div>
          <div className="a4-cover-sub">
            Proposta economica per
            <span className="a4-cover-client">«{clientName}»</span>
          </div>
        </div>
      </div>

      {/* real pages */}
      {pages.map((entries, p) => (
        <div className="a4-page" data-testid="a4-page" key={p}>
          {p === 0 ? (
            <Band logo={logo} quote={quote} />
          ) : (
            <RunHeader logo={logo} quote={quote} />
          )}
          <div className="a4-col">
            {entries.map((entry, idx) => {
              const b = blockMap[entry.key];
              if (!b) return null;
              return (
                <div className="a4-blk" key={`${entry.key}-${idx}`}>
                  {entry.kind === "item" ? (
                    <QuoteItem
                      it={b.item}
                      alt={b.alt}
                      description={entry.description}
                      descriptionOffset={entry.descriptionOffset || 0}
                      continuation={entry.continuation}
                      suffix={suffix}
                    />
                  ) : (
                    b.node
                  )}
                </div>
              );
            })}
          </div>
          <Footer page={p + 1} total={total} />
        </div>
      ))}
    </div>
  );
};
